import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  ListItemIcon,
  Typography,
  Tooltip
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { ColorModeContext } from "../../contexts";
import HelpIcon from "@mui/icons-material/Help";
import EmailIcon from "@mui/icons-material/Email";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { Link } from "react-router-dom";
import { useProSidebar } from "react-pro-sidebar";
import { borderRadius } from "../../constants";
import IULogo from "../branding/IULogo";
import profilePic from "../../assets/images/profile_pic.svg";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { useSelector, useDispatch } from "react-redux";
import FeedbackModal from "../modals/FeedbackModal";
import AssessmentIcon from '@mui/icons-material/Assessment';
import PolicyIcon from '@mui/icons-material/Policy';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import ErrorIcon from "@mui/icons-material/Error";
import { useLocation } from "react-router-dom";
import { setSelectedAsset } from "../../features/userAssets/userAssetsSlice";
import { toggleTheme, selectThemeMode } from "../../features/theme/themeSlice";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";

import packages from '../../../package.json';

const MenuItemComponent = ({
  onClick = () => { },
  IconComponent = null,
  text,
  handleClose
}) => (
  <MenuItem onClick={() => {
    onClick();
    if (typeof handleClose === 'function') {
      handleClose();
    }
  }}>
    {IconComponent && (
      <ListItemIcon>
        <IconComponent />
      </ListItemIcon>
    )}
    {text}
  </MenuItem>
);

const Topbar = ({
  user,
  signOut,
  toggleDrawer,
  open,
  runOnboarding,
  setRunOnboarding,
  setSnackbarInfo
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [openFeedbackModal, setOpenFeedbackModal] = useState(false);
  const { collapseSidebar } = useProSidebar();
  const { enableAI } = useSelector((store) => store.settings);
  const selectedAsset = useSelector((store) => store.userAssets.selectedAsset);
  const themeMode = useSelector(selectThemeMode);
  const dispatch = useDispatch();
  const location = useLocation();
  const isQueryBuilderRoute = location.pathname.includes('/queryBuilder');
  const [licenseStatus, setLicenseStatus] = useState({
    status: "loading",
    color: "info",
    icon: InfoIcon,
    label: "Checking..."
  });

  const handleMenu = (event) => {
    setAnchorEl(!anchorEl);
  };

  useEffect(() => {
    if (user && user.attributes) {
      setUserInfo(user.attributes);
    }
  }, []);

  useEffect(() => {
    updateLicenseStatus();
  }, [selectedAsset]);

  useEffect(() => {
    if (!isQueryBuilderRoute && selectedAsset) {
      dispatch(setSelectedAsset(null));
    }
  }, [isQueryBuilderRoute, dispatch, selectedAsset]);

  const handleClose = () => {
    setAnchorEl(!anchorEl);
  };

  const handleFeedback = () => {
    setOpenFeedbackModal(!openFeedbackModal);
  };

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
  };

  const updateLicenseStatus = () => {
    // Default state when no data is available
    if (!selectedAsset) {
      setLicenseStatus({
        status: "unknown",
        color: "disabled",
        icon: InfoIcon,
        label: "Unknown"
      });
      return;
    }

    try {
      const expirationDate = new Date(selectedAsset.ExpirationDate);

      // Check if the date is valid
      if (isNaN(expirationDate.getTime())) {
        setLicenseStatus({
          status: "unknown",
          color: "disabled",
          icon: InfoIcon,
          label: "Unknown"
        });
        return;
      }

      const now = new Date();
      const timeLeft = expirationDate.getTime() - now.getTime();
      const daysLeft = Math.ceil(timeLeft / (1000 * 3600 * 24));

      if (daysLeft > 7) {
        setLicenseStatus({
          status: "valid",
          color: "success",
          icon: CheckCircleIcon,
          label: "Active"
        });
      } else if (daysLeft >= 0) {
        setLicenseStatus({
          status: "expiring",
          color: "warning",
          icon: WarningIcon,
          label: `Expires in ${daysLeft} days`
        });
      } else {
        setLicenseStatus({
          status: "expired",
          color: "error",
          icon: ErrorIcon,
          label: "Expired"
        });
      }
    } catch (error) {
      console.error("Error processing license expiration:", error);
      setLicenseStatus({
        status: "error",
        color: "disabled",
        icon: InfoIcon,
        label: "Unknown"
      });
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", pl: 2, color: "text.primary" }}>
          <IULogo height={38} />
        </Box>
        <Box
          display={"flex"}
          sx={{
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
            <Tooltip title={themeMode === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}>
              <IconButton
                onClick={handleThemeToggle}
                sx={{
                  height: 40,
                  width: 40,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  }
                }}
              >
                {themeMode === 'light'
                  ? <DarkModeIcon />
                  : <LightModeIcon />
                }
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{
            display: "flex", alignItems: "center"
          }}>
            {isQueryBuilderRoute && <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mr: 2,
                backgroundColor:
                  licenseStatus.color === "success" ? alpha(theme.palette.success.main, 0.1) :
                    licenseStatus.color === "warning" ? alpha(theme.palette.warning.main, 0.1) :
                      licenseStatus.color === "error" ? alpha(theme.palette.error.main, 0.1) :
                        alpha(theme.palette.grey[500], 0.1),
                borderRadius: theme.shape.borderRadius,
                padding: "4px 12px",
              }}
            >
              {licenseStatus.icon && (
                <licenseStatus.icon
                  color={licenseStatus.color}
                  fontSize="small"
                  sx={{ mr: 1 }}
                />
              )}
              <Typography
                variant="body2"
                sx={{
                  color:
                    licenseStatus.color === "success" ? "success.main" :
                      licenseStatus.color === "warning" ? "warning.main" :
                        licenseStatus.color === "error" ? "error.main" :
                          "text.secondary",
                  fontWeight: 500
                }}
              >
                Access: {licenseStatus.status === "valid" ? "Active" :
                  licenseStatus.status === "expiring" ? "Expiring Soon" :
                    licenseStatus.status === "expired" ? "Expired" : "Unknown"}
              </Typography>
            </Box>}
            <Button
              onClick={() => window.open('https://iu.co1.qualtrics.com/jfe/form/SV_4ScahaRGu8yW1LM', '_blank', 'noopener,noreferrer')}
              startIcon={<EmailIcon fontSize="small" />}
              sx={{
                color: "text.primary",
                textTransform: "none",
                fontWeight: 500,
                mr: 0.5,
                '& .MuiButton-startIcon': { color: "text.secondary" },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: "primary.main",
                  '& .MuiButton-startIcon': { color: "primary.main" },
                },
              }}
            >
              Feedback
            </Button>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={() => handleMenu()}
              sx={{
                bgcolor: "none",
                borderRadius: borderRadius,
                ml: 0.5,
              }}
            >
              <Avatar
                alt="Profile Picture"
                src={profilePic}
                sx={{
                  height: "30px",
                  width: "30px",
                  backgroundColor: theme.palette.info.light,
                }}
              />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={anchorEl}
              onClose={() => handleClose()}
              slotProps={{
                paper: {
                  sx: {
                    boxShadow:
                      '0px 8px 24px rgba(0,0,0,0.12), 0px 4px 8px rgba(0,0,0,0.08)',
                    borderRadius: 2,
                    mt: 3,
                  },
                },
              }}
            >
              <MenuItemComponent text={user?.attributes?.name} handleClose={handleClose} />
              <Divider />
              {/* <Link
                to="/profile"
                className="router-link"
                state={{ userInfo, cameFrom: "appbar" }}
              >
                <MenuItemComponent
                  onClick={() => handleClose()}
                  IconComponent={AccountCircleIcon}
                  text="Profile"
                />
              </Link> */}
              <Link
                to="/faq"
                className="router-link"
                state={{ userInfo, cameFrom: "appbar" }}
              >
                <MenuItemComponent IconComponent={HelpIcon} text="FAQ" handleClose={handleClose} />
              </Link>
              <Link
                to="/data-overview"
                className="router-link"
                state={{ userInfo, cameFrom: "appbar" }}
              >
                <MenuItemComponent IconComponent={AssessmentIcon} text="Data Overview" handleClose={handleClose} />
              </Link>
              <Link
                to="/how-to"
                className="router-link"
                state={{ userInfo, cameFrom: "appbar" }}
              >
                <MenuItemComponent IconComponent={SummarizeIcon} text="How To's" handleClose={handleClose} />
              </Link>
              <Link
                to="/data-deidentification"
                className="router-link"
                state={{ userInfo, cameFrom: "appbar" }}
              >
                <MenuItemComponent IconComponent={PolicyIcon} text="Data De-Identification" handleClose={handleClose} />
              </Link>
              <MenuItemComponent
                handleClose={handleClose}
                onClick={() => signOut()}
                IconComponent={ExitToAppIcon}
                text="Log Out"
              />
              <Divider />
              <MenuItemComponent text={`Release ${packages.version}`} handleClose={handleClose} />
            </Menu>
          </Box>
        </Box>
      </Box>
      {openFeedbackModal &&
        <FeedbackModal
          open={openFeedbackModal}
          setOpen={setOpenFeedbackModal}
          userName={user?.attributes?.name}
          setSnackbarInfo={setSnackbarInfo}
        />
      }
    </>
  );
};

export default Topbar;
